/**
 * GoalStepsChecklist — full interactive checklist for detail sheet.
 * Single-column, spacious layout with completion date and animated check.
 */

import { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { motion } from 'framer-motion'
import type { Goal, GoalEntry } from '@turnifly/shared/types'
import { useToggleStep } from '@turnifly/shared/hooks'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'

interface GoalStepsChecklistProps {
  goal: Goal
  entries: GoalEntry[]
  isOwner: boolean
}

export function GoalStepsChecklist({
  goal,
  entries,
  isOwner,
}: GoalStepsChecklistProps): React.ReactElement {
  const { t } = useTranslation()
  const toggleStep = useToggleStep()

  const completedMap = useMemo((): Map<number, GoalEntry> => {
    const map = new Map<number, GoalEntry>()
    for (const e of entries) {
      if (e.step_index !== null) map.set(e.step_index, e)
    }
    return map
  }, [entries])

  const steps = goal.steps ?? []

  const handleToggle = useCallback((index: number, label: string, isCompleted: boolean): void => {
    if (!isOwner || toggleStep.isPending) return
    hapticImpact('light')
    toggleStep.mutate({ goalId: goal.id, stepIndex: index, label, isCompleting: !isCompleted })
  }, [isOwner, goal.id, toggleStep])

  if (steps.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-text-hint/50">
        {t('goals.noEntries')}
      </p>
    )
  }

  return (
    <div className="space-y-1.5">
      {steps.map((step, index) => {
        const entry        = completedMap.get(index)
        const isCompleted  = !!entry
        const completedDate = entry
          ? format(new Date(entry.entry_date + 'T00:00:00'), 'dd.MM')
          : null

        return (
          <motion.button
            key={index}
            type="button"
            disabled={!isOwner || toggleStep.isPending}
            onClick={() => handleToggle(index, step.label, isCompleted)}
            whileTap={isOwner ? { scale: 0.985 } : undefined}
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all tap-highlight-transparent ${
              isCompleted
                ? 'bg-success/[0.05] border border-success/[0.12]'
                : 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05]'
            } ${!isOwner ? 'cursor-default' : ''}`}
          >
            {/* Animated checkbox */}
            <div className={`flex-shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
              isCompleted
                ? 'bg-success border-success'
                : 'border-white/[0.18] bg-transparent'
            }`}>
              {isCompleted && (
                <motion.svg
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.3 }}
                  viewBox="0 0 16 16"
                  className="w-3 h-3 text-white"
                >
                  <motion.path
                    d="M3 8l3 3 7-7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </motion.svg>
              )}
            </div>

            {/* Step number + label */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-text-hint/40 tabular-nums shrink-0">{index + 1}.</span>
                <span className={`text-sm leading-snug ${
                  isCompleted ? 'text-text-hint line-through' : 'text-text'
                }`}>
                  {step.label}
                </span>
              </div>
            </div>

            {/* Completion date */}
            {completedDate && (
              <span className="shrink-0 text-[11px] text-success/60 font-medium tabular-nums">
                {completedDate}
              </span>
            )}
          </motion.button>
        )
      })}
    </div>
  )
}
